/**
 * The View-as write policy (M4S-5670, docs/pdlc/cs-view-as-client.md §6).
 *
 * While staff view Studio as a client, every write the browser sends goes
 * through one rule: `isViewAsWriteAllowed`. It is default deny. A write is
 * allowed only when a row below names it, and each allowed write says whether
 * the backstop records it (`api_write` or `data_export`) or not (telemetry and
 * staff preferences).
 *
 * Pure on purpose: no React, no store, no network. The three browser
 * backstops (axios, GraphQL, same-origin fetch) call it today, and the Next
 * request proxy can apply the same rule later (API-3) without drift.
 *
 * Paths arrive the way each client sends them: relative to the axios instance's
 * base URL (`/studio-ai/...` for the portal, which is `.../studio/v1`), or as a
 * same-origin `/api/...` URL for Next. `normaliseViewAsPath` strips the origin,
 * the query string, the hash and a trailing slash before any rule reads them.
 */

import { Kind, OperationTypeNode, parse } from 'graphql';

/** Which browser client sent the write. Named from the axios instance, never guessed from a URL. */
export type ViewAsWriteClient = 'portal' | 'platform' | 'microservice' | 'graphql' | 'next';

/** What the backstop records for an allowed write once it succeeds (2xx). */
export type ViewAsWriteRecord = 'api_write' | 'data_export' | 'none';

/** Why a backstop refused: the policy (View-as stays on) or the session (part of the end path). */
export type ViewAsRefusalReason = 'policy' | 'session';

export interface ViewAsWriteInput {
    client: ViewAsWriteClient;
    method: string;
    path: string;
    /** The parsed JSON body, a JSON string, or anything else (ignored). */
    body?: unknown;
    /** GraphQL only: the mutation's root field names, comma-separated. */
    operation?: string | null;
}

export interface ViewAsWriteDecision {
    allowed: boolean;
    record: ViewAsWriteRecord;
    /** The resource id in the path (layout key, conversation id …), else the normalised path. */
    ref: string | null;
    /** A short name for the row's `detail.kind`, or for the refusal. */
    kind: string | null;
    /** Ids involved, for the action row's `detail` (data exports name what was exported). */
    detail?: Record<string, unknown>;
}

/** The toast a policy refusal shows. */
export function viewAsRefusalMessage(clientName: string | null | undefined): string {
    const name = clientName?.trim() || 'a client';
    return `Not available while viewing as ${name}. Clear View as to do this as yourself.`;
}

/**
 * Thrown (as a rejection) by a browser backstop before a refused write leaves
 * the tab. `policy`: the action is blocked under View-as and View-as stays on.
 * `session`: the tab's session is not active or its freshness check failed;
 * the end path explains it after its reload, so no toast of its own.
 */
export class ViewAsWriteRefusedError extends Error {
    readonly reason: ViewAsRefusalReason;
    readonly client: ViewAsWriteClient;
    readonly method: string;
    readonly path: string;

    constructor(init: {
        reason: ViewAsRefusalReason;
        client: ViewAsWriteClient;
        method: string;
        path: string;
        message?: string;
    }) {
        super(
            init.message ??
                (init.reason === 'policy'
                    ? viewAsRefusalMessage(null)
                    : 'View as is not active in this tab.'),
        );
        this.name = 'ViewAsWriteRefusedError';
        this.reason = init.reason;
        this.client = init.client;
        this.method = init.method;
        this.path = init.path;
    }
}

export function isViewAsWriteRefusedError(error: unknown): error is ViewAsWriteRefusedError {
    return error instanceof ViewAsWriteRefusedError;
}

// ---------------------------------------------------------------------------
// Normalising
// ---------------------------------------------------------------------------

/**
 * A path as the rules read it: no origin, query string or hash, no trailing
 * slash, `.` and `..` segments resolved, and a leading slash. With `basePath`
 * (an axios instance's base URL path, e.g. `/studio/v1`), an absolute URL that
 * names the same API has that prefix removed, so it reads like a relative one.
 */
export function normaliseViewAsPath(url: string, basePath?: string): string {
    let path = String(url ?? '').trim();
    const hadOrigin = /^[a-z][a-z0-9+.-]*:\/\//i.test(path) || path.startsWith('//');
    path = path.replace(/^[a-z][a-z0-9+.-]*:\/\/[^/?#]*/i, '').replace(/^\/\/[^/?#]*/, '');
    path = path.split('#')[0].split('?')[0];

    const segments: string[] = [];
    for (const segment of path.split('/')) {
        if (segment === '' || segment === '.') continue;
        if (segment === '..') segments.pop();
        else segments.push(segment);
    }
    let normalised = `/${segments.join('/')}`;

    const base = basePath ? normaliseViewAsPath(basePath) : '';
    if (hadOrigin && base && base !== '/') {
        if (normalised === base) normalised = '/';
        else if (normalised.startsWith(`${base}/`)) normalised = normalised.slice(base.length);
    }
    return normalised;
}

function safeDecode(value: string): string {
    try {
        return decodeURIComponent(value);
    } catch {
        return value;
    }
}

/** A JSON body as an object, whether it arrived parsed or as a string. Anything else is null. */
export function parseViewAsBody(body: unknown): Record<string, unknown> | null {
    let value = body;
    if (typeof value === 'string') {
        try {
            value = JSON.parse(value);
        } catch {
            return null;
        }
    }
    if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
    if (typeof FormData !== 'undefined' && value instanceof FormData) return null;
    if (typeof Blob !== 'undefined' && value instanceof Blob) return null;
    if (ArrayBuffer.isView(value) || value instanceof ArrayBuffer) return null;
    if (typeof URLSearchParams !== 'undefined' && value instanceof URLSearchParams) return null;
    return value as Record<string, unknown>;
}

function str(value: unknown): string | null {
    return typeof value === 'string' && value.trim() ? value.trim() : null;
}

// ---------------------------------------------------------------------------
// GraphQL
// ---------------------------------------------------------------------------

/**
 * Whether a GraphQL document writes. Returns null for a query (a read). For a
 * mutation or subscription, returns its root field names joined by commas; a
 * document that cannot be read, or that selects through a fragment at the root,
 * is treated as a write with an unknown operation, so the policy refuses it.
 */
export function graphqlWriteOperation(document: string): string | null {
    let parsed;
    try {
        parsed = parse(document);
    } catch {
        return '<unparsed>';
    }
    const operations = parsed.definitions.filter(
        (definition) => definition.kind === Kind.OPERATION_DEFINITION,
    );
    if (operations.length === 0) return '<unparsed>';
    const writes = operations.filter(
        (operation) =>
            operation.kind === Kind.OPERATION_DEFINITION &&
            operation.operation !== OperationTypeNode.QUERY,
    );
    if (writes.length === 0) return null;
    const fields: string[] = [];
    for (const operation of writes) {
        if (operation.kind !== Kind.OPERATION_DEFINITION) continue;
        for (const selection of operation.selectionSet.selections) {
            fields.push(selection.kind === Kind.FIELD ? selection.name.value : '<fragment>');
        }
    }
    return fields.length > 0 ? fields.join(',') : '<unparsed>';
}

// ---------------------------------------------------------------------------
// The rules
// ---------------------------------------------------------------------------

type Method = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

interface Rule {
    /** For the row's `detail.kind`, or for the refusal. */
    kind: string;
    methods: readonly Method[];
    pattern: RegExp;
    /** `block` rules exist so an allow pattern can never reach them; default deny would also refuse. */
    record: ViewAsWriteRecord | 'block';
    /** Extra condition on the body. A rule whose condition fails falls through to the next rule. */
    when?: (body: Record<string, unknown> | null) => boolean;
    /** The ref. Defaults to the first path capture, else the normalised path. */
    ref?: (
        match: RegExpMatchArray,
        body: Record<string, unknown> | null,
        path: string,
    ) => string | null;
    detail?: (
        match: RegExpMatchArray,
        body: Record<string, unknown> | null,
    ) => Record<string, unknown>;
}

const WRITE: readonly Method[] = ['POST', 'PUT', 'PATCH', 'DELETE'];
/** A single path segment: an id or key. */
const ID = '([^/]+)';
/** Words the legacy crosstab API uses as a path's first segment, never a layout key. */
const CROSSTAB_RESERVED = '(?!(?:publish|export|create|import|open-end|org)(?:/|$))';

const shareTypeOf = (body: Record<string, unknown> | null) => str(body?.shareType);

/**
 * Portal API (`portalApi`, base `.../studio/v1`). The §6 portalApi allow list,
 * plus explicit blocks for the deletes and toggles an allow pattern sits next to.
 */
const PORTAL_RULES: readonly Rule[] = [
    // DANI — blocked: deletes and the "Anyone with the link" share.
    {
        kind: 'dani_conversation_delete',
        methods: ['DELETE'],
        pattern: new RegExp(`^/studio-ai/conversations/${ID}$`),
        record: 'block',
    },
    {
        kind: 'dani_folder_delete',
        methods: ['DELETE'],
        pattern: new RegExp(`^/studio-ai/folders/${ID}$`),
        record: 'block',
    },
    // DANI — allowed and recorded.
    {
        kind: 'dani_stop',
        methods: ['POST'],
        pattern: new RegExp(`^/studio-ai/conversations/${ID}/stop$`),
        record: 'api_write',
    },
    {
        kind: 'dani_rename',
        methods: ['PUT'],
        pattern: new RegExp(`^/studio-ai/conversations/${ID}/rename$`),
        record: 'api_write',
    },
    {
        kind: 'dani_folder_move',
        methods: ['PUT'],
        pattern: new RegExp(`^/studio-ai/conversations/${ID}/folder$`),
        record: 'api_write',
    },
    {
        kind: 'dani_folder_create',
        methods: ['POST'],
        pattern: /^\/studio-ai\/folders$/,
        record: 'api_write',
    },
    {
        kind: 'dani_folder_update',
        methods: ['PUT'],
        pattern: new RegExp(`^/studio-ai/folders/${ID}$`),
        record: 'api_write',
    },
    {
        kind: 'dani_feedback',
        methods: ['POST'],
        pattern: /^\/studio-ai\/feedback$/,
        record: 'api_write',
        detail: (_m, body) => ({
            conversationId: str(body?.conversationId),
            messageId: str(body?.messageId),
        }),
    },
    {
        kind: 'dani_feedback_read',
        methods: ['PUT'],
        pattern: new RegExp(`^/studio-ai/feedback/${ID}/read$`),
        record: 'api_write',
    },
    {
        kind: 'dani_share',
        methods: ['POST'],
        pattern: new RegExp(`^/studio-ai/conversations/${ID}/share$`),
        record: 'api_write',
        when: (body) => shareTypeOf(body) === 'user' || shareTypeOf(body) === 'organization',
        detail: (_m, body) => ({ shareType: shareTypeOf(body) }),
    },
    // Every other share type, `link` included, or a share with no readable type.
    {
        kind: 'dani_share_link',
        methods: ['POST'],
        pattern: new RegExp(`^/studio-ai/conversations/${ID}/share$`),
        record: 'block',
    },
    // Revoking a share reduces exposure.
    {
        kind: 'dani_share_revoke',
        methods: ['DELETE'],
        pattern: new RegExp(`^/studio-ai/shares/${ID}$`),
        record: 'api_write',
    },
    {
        kind: 'query_data',
        methods: ['POST'],
        pattern: /^\/studio-ai\/export-query-data$/,
        record: 'data_export',
        ref: (_m, body, path) => str(body?.conversationId) ?? path,
        detail: (_m, body) => ({
            conversationId: str(body?.conversationId),
            messageId: str(body?.messageId),
        }),
    },
    // The report page records `report_export` itself on success (§5), so the backstop does not.
    {
        kind: 'report_export',
        methods: ['POST'],
        pattern: new RegExp(`^/studio-ai/reports/${ID}/export$`),
        record: 'none',
    },
    // Telemetry and preferences: allowed, not recorded.
    {
        kind: 'release_notes',
        methods: ['POST'],
        pattern: /^\/studio-ai\/release-notes\/(?:acknowledge|dismiss)$/,
        record: 'none',
    },
    {
        kind: 'voice_transcription',
        methods: ['POST'],
        pattern: /^\/studio-ai\/transcribe$/,
        record: 'none',
    },
    { kind: 'route_log', methods: ['POST'], pattern: /^\/users\/log-route$/, record: 'none' },
    // Funds is a read surface (§5). The API names this "create an invoice", but it
    // only renders invoice HTML from the transaction and stores nothing.
    {
        kind: 'funds_invoice_view',
        methods: ['POST'],
        pattern: new RegExp(`^/transactions/invoice/${ID}$`),
        record: 'none',
    },
    {
        kind: 'preference',
        methods: ['POST', 'PUT', 'PATCH'],
        pattern: /^\/users\/me\/preferences\/.+$/,
        record: 'none',
    },
    {
        kind: 'zendesk_ticket',
        methods: ['POST'],
        pattern: /^\/zendesk\/ticket$/,
        record: 'api_write',
    },
    // Project data export (can include panelist ids).
    {
        kind: 'project_export',
        methods: ['POST'],
        pattern: /^\/export$/,
        record: 'data_export',
        ref: (_m, _body, path) => path,
        detail: (_m, body) => ({
            exportType: str(body?.exportType),
            surveyIds: Array.isArray(body?.surveyIds) ? body.surveyIds.slice(0, 50) : null,
            viewFilterId: body?.viewFilterId ?? null,
            questionCount: Array.isArray(body?.questionIds) ? body.questionIds.length : null,
            panelistCount: Array.isArray(body?.panelistIds) ? body.panelistIds.length : null,
        }),
    },
    // Document upload: the browser mints a presigned POST here (a GET), then
    // uploads straight to S3, cross-origin. The mint is the only call the
    // backstops see, so it is the one recorded. Delete and Publish stay blocked.
    {
        kind: 'document_upload',
        methods: ['GET'],
        pattern: new RegExp(`^/projects/${ID}/documents/file/upload$`),
        record: 'api_write',
        detail: (m) => ({ projectId: safeDecode(m[1]) }),
    },
    {
        kind: 'document_publish',
        methods: ['PATCH'],
        pattern: new RegExp(`^/projects/${ID}/documents/file/${ID}/publicity$`),
        record: 'block',
    },
    {
        kind: 'document_delete',
        methods: ['DELETE'],
        pattern: new RegExp(`^/projects/${ID}/documents/(?:file|folder)/${ID}$`),
        record: 'block',
    },
    // Legacy crosstab — blocked: publish (a toggle), delete, OE upload.
    {
        kind: 'crosstab_publish',
        methods: WRITE,
        pattern: new RegExp(`^/crosstab/publish/${ID}$`),
        record: 'block',
    },
    {
        kind: 'crosstab_oe_upload',
        methods: WRITE,
        pattern: /^\/crosstab\/open-end\/upload(?:\/.*)?$/,
        record: 'block',
    },
    {
        kind: 'crosstab_delete',
        methods: ['DELETE'],
        pattern: new RegExp(`^/crosstab/${CROSSTAB_RESERVED}${ID}$`),
        record: 'block',
    },
    // Legacy crosstab — allowed and recorded.
    {
        kind: 'crosstab_create',
        methods: ['POST'],
        pattern: new RegExp(`^/crosstab/create/${ID}(?:/${ID})?$`),
        record: 'api_write',
        ref: (_m, _body, path) => path,
        detail: (m) => ({ projectId: safeDecode(m[1]), surveyId: m[2] ? safeDecode(m[2]) : null }),
    },
    {
        kind: 'crosstab_duplicate',
        methods: ['POST'],
        pattern: new RegExp(`^/crosstab/import/${ID}/${ID}(?:/${ID})?$`),
        record: 'api_write',
        detail: (m) => ({ sourceKey: safeDecode(m[1]), projectId: safeDecode(m[2]) }),
    },
    {
        kind: 'crosstab_rename',
        methods: ['PATCH'],
        pattern: new RegExp(`^/crosstab/${CROSSTAB_RESERVED}${ID}/rename$`),
        record: 'api_write',
    },
    {
        kind: 'crosstab_save',
        methods: ['PATCH'],
        pattern: new RegExp(`^/crosstab/${CROSSTAB_RESERVED}${ID}$`),
        record: 'api_write',
    },
    {
        kind: 'crosstab_export',
        methods: ['POST'],
        pattern: new RegExp(`^/crosstab/export/${ID}$`),
        record: 'data_export',
        detail: (m) => ({ layoutKey: safeDecode(m[1]) }),
    },
    // The credits pre-check (M4S-5912, credits-ui.md §6): a read sent as POST. It decides on the
    // client's balance and writes nothing, the denial log included, so it is not recorded.
    {
        kind: 'credit_spend_check',
        methods: ['POST'],
        pattern: /^\/credit-consumption\/spend-check$/,
        record: 'none',
    },
    // Credits activity export (M4S-5895 step 26, docs/pdlc/credits-ui.md §7 footer and
    // "View-as"): a GET, so never refused, but the file leaves with the client's usage in it,
    // so under View-as it is recorded as a data export. The client copy (`view=client`) and the
    // internal copy share the path; the query string is not part of the rule.
    {
        kind: 'credits_activity_export',
        methods: ['GET'],
        pattern: new RegExp(`^/admin/orgs/${ID}/credit-consumption/activity/export$`),
        record: 'data_export',
        detail: (m) => ({ orgId: safeDecode(m[1]) }),
    },
    {
        kind: 'credits_activity_export',
        methods: ['GET'],
        pattern: /^\/credit-consumption\/activity\/export$/,
        record: 'data_export',
        ref: () => 'own-org',
    },
    // Credits metering (D23). Without it default deny would stop every PPTX deck.
    {
        kind: 'metering',
        methods: ['POST'],
        pattern: new RegExp(`^/credit-consumption/observe/${ID}$`),
        record: 'api_write',
        detail: (m, body) => ({
            functionKey: safeDecode(m[1]),
            ...(str(body?.layoutKey) ? { layoutKey: str(body?.layoutKey) } : {}),
            ...(str(body?.path) ? { exportPath: str(body?.path) } : {}),
        }),
    },
    // The purpose tag on a DANI conversation (M4S-5912, credits-ui.md §6). It labels usage the
    // conversation's owner is charged for; the API lets only the owner tag, and only once.
    {
        kind: 'credit_purpose_tag',
        methods: ['PUT'],
        pattern: new RegExp(`^/credit-consumption/conversations/${ID}/purpose$`),
        record: 'api_write',
        ref: (m) => safeDecode(m[1]),
        detail: (_m, body) => ({
            purpose: str(body?.purpose),
            ...(str(body?.refId) ? { refId: str(body?.refId) } : {}),
        }),
    },
];

/** Microservice gateway (`microserviceApi`): every write blocked except the recorded Excel export. */
const MICROSERVICE_RULES: readonly Rule[] = [
    {
        kind: 'excel_export',
        methods: ['POST'],
        pattern: /^\/excel\/export$/,
        record: 'data_export',
        ref: (_m, _body, path) => path,
        detail: (_m, body) => ({
            projectName: str(body?.projectName),
            surveysIncluded: Array.isArray(body?.surveysIncluded)
                ? body.surveysIncluded.slice(0, 50)
                : null,
            questionName: str(body?.questionName),
        }),
    },
];

/** Platform API (`studioApi`): deposit, vTracker, email verification. Every write blocked. */
const PLATFORM_RULES: readonly Rule[] = [];

/** Staff-personal saved views; the only GraphQL mutations allowed. */
const GRAPHQL_ALLOWED_FIELDS: ReadonlySet<string> = new Set([
    'createViewFilter',
    'updateViewFilter',
    'deleteViewFilter',
]);

/**
 * Next routes (`/api/*`, same origin). The §6 Next allow list, the reads that
 * are sent as POST (they compute and write nothing), and the telemetry and
 * session routes the inventory found. Everything else is refused, including
 * `upload-by-project`, crosstab DELETE, My Work DELETE, `/api/dani/reel-studio/*`,
 * `/api/encrypt/builder` and crosstab publish.
 */
const NEXT_RULES: readonly Rule[] = [
    // Blocked, named so no allow pattern below can reach them.
    {
        kind: 'ai_crosstab_publish',
        methods: WRITE,
        pattern: /^\/api\/studio\/v1\/crosstab\/publish(?:\/.*)?$/,
        record: 'block',
    },
    {
        kind: 'crosstab_oe_upload',
        methods: WRITE,
        pattern: /^\/api\/studio\/v1\/crosstab\/open-end(?:\/.*)?$/,
        record: 'block',
    },
    {
        kind: 'ai_crosstab_delete',
        methods: ['DELETE'],
        pattern: /^\/api\/studio\/v1\/crosstab(?:\/.*)?$/,
        record: 'block',
    },
    {
        kind: 'reel_studio',
        methods: WRITE,
        pattern: /^\/api\/dani\/reel-studio(?:\/.*)?$/,
        record: 'block',
    },
    // M4S-5777 X55: a project's custom variables are shared by the client's people; a View-as
    // session never adds, changes or deletes one. Reading the list is not a write.
    {
        kind: 'crosstab_project_variable',
        methods: WRITE,
        pattern: new RegExp(`^/api/crosstab/project/${ID}/variables$`),
        record: 'block',
    },
    // M4S-5777 X128: the staff admin switch for DANI in AI Crosstabs is never changed while
    // viewing as a client. Reading the switch is not a write.
    {
        kind: 'crosstab_dani_setting',
        methods: WRITE,
        pattern: /^\/api\/crosstab\/dani-setting\/admin$/,
        record: 'block',
    },
    // The staff member's own demo token (`useDemoCookie`, body `{ jwtData: { email } }`):
    // it names no org or survey, so it is staff-personal. Builder's embed token
    // names a target org or a survey and stays refused.
    {
        kind: 'builder_demo_token',
        methods: ['POST'],
        pattern: /^\/api\/encrypt\/builder$/,
        record: 'none',
        when: (body) =>
            body != null &&
            body.jwtData != null &&
            body.targetOrgId == null &&
            body.surveyId == null,
    },
    { kind: 'builder', methods: WRITE, pattern: /^\/api\/encrypt\/builder$/, record: 'block' },
    // Builder's own routes (D10: Builder under View-as is blocked). The editor and the DANI
    // chat's survey cards (save, fix, preview, feasibility record, publish, launch, fielding,
    // the survey author and reader) all write through them, so one row refuses them all.
    // Reads (GET: the draft, its price, its fielding) stay open.
    {
        kind: 'builder_native',
        methods: WRITE,
        pattern: /^\/api\/studio\/v1\/builder(?:\/.*)?$/,
        record: 'block',
    },
    { kind: 'payments', methods: WRITE, pattern: /^\/api\/payments(?:\/.*)?$/, record: 'block' },

    // View-as's own routes. The fetch backstop passes them before the policy;
    // listed so the Next proxy (API-3) reads the same rule.
    { kind: 'view_as', methods: WRITE, pattern: /^\/api\/view-as\/.+$/, record: 'none' },
    // NextAuth: sign-out, session update, client error log.
    { kind: 'auth_session', methods: ['POST'], pattern: /^\/api\/auth\/.+$/, record: 'none' },

    // AI Crosstabs: edit, autosave, rename (allowed, recorded). A keyless POST
    // creates a staff draft, or under `convert=` a conversion handed to the
    // client by `/api/view-as/crosstab-handover` (D21).
    {
        kind: 'ai_crosstab_save',
        methods: ['POST'],
        pattern: new RegExp(`^/api/crosstab/dani-layout/project/${ID}$`),
        record: 'api_write',
        when: (body) => str(body?.key) != null,
        ref: (_m, body) => str(body?.key),
        detail: (m, body) => ({ projectId: safeDecode(m[1]), layoutKey: str(body?.key) }),
    },
    {
        kind: 'ai_crosstab_create',
        methods: ['POST'],
        pattern: new RegExp(`^/api/crosstab/dani-layout/project/${ID}$`),
        record: 'api_write',
        ref: (_m, _body, path) => path,
        detail: (m) => ({ projectId: safeDecode(m[1]) }),
    },
    {
        kind: 'ai_crosstab_rename',
        methods: ['PATCH'],
        pattern: new RegExp(`^/api/studio/v1/crosstab/${CROSSTAB_RESERVED}${ID}/rename$`),
        record: 'api_write',
    },
    {
        kind: 'ai_crosstab_save',
        methods: ['PATCH'],
        pattern: new RegExp(`^/api/studio/v1/crosstab/${CROSSTAB_RESERVED}${ID}$`),
        record: 'api_write',
    },
    // M4S-5777 X44: a filter saved by name for the project. The API does not know the tab views
    // as a client (API-3), so a save would be the staff member's (no org) and never reach the
    // client's list, and a delete would remove a row the client sees: both refused.
    {
        kind: 'crosstab_saved_filter',
        methods: WRITE,
        pattern: new RegExp(`^/api/crosstab/saved-filters(?:/.*)?$`),
        record: 'block',
    },
    // M4S-5777 X120: a client's own data file (respondent rows). An upload would be stored as the
    // staff member's (no org), which the client never reads, and a delete removes the client's
    // data: both refused while viewing as a client.
    {
        kind: 'crosstab_outside_data',
        methods: WRITE,
        pattern: /^\/api\/crosstab\/outside-data(?:\/.*)?$/,
        record: 'block',
    },
    // M4S-5777 X136: naming a version of a crosstab, and restoring one. A restore is an ordinary
    // save of the version (the same rights as `ai_crosstab_save`), so both are allowed and recorded.
    {
        kind: 'ai_crosstab_version_name',
        methods: ['POST'],
        pattern: new RegExp(`^/api/crosstab/layout/${ID}/versions$`),
        record: 'api_write',
        ref: (m) => safeDecode(m[1]),
    },
    {
        kind: 'ai_crosstab_version_name',
        methods: ['PATCH'],
        pattern: new RegExp(`^/api/crosstab/layout/${ID}/versions/${ID}$`),
        record: 'api_write',
        ref: (m) => safeDecode(m[1]),
        detail: (m) => ({ layoutKey: safeDecode(m[1]), versionId: safeDecode(m[2]) }),
    },
    {
        kind: 'ai_crosstab_restore',
        methods: ['POST'],
        pattern: new RegExp(`^/api/crosstab/layout/${ID}/versions/${ID}/restore$`),
        record: 'api_write',
        ref: (m) => safeDecode(m[1]),
        detail: (m) => ({ layoutKey: safeDecode(m[1]), versionId: safeDecode(m[2]) }),
    },
    // M4S-5777 X102: the staff member's own crosstab display preferences, keyed server-side by
    // their idToken, never the client's. A staff preference, like `/users/me/preferences`.
    {
        kind: 'crosstab_user_pref',
        methods: ['PUT'],
        pattern: /^\/api\/crosstab\/preferences$/,
        record: 'none',
    },

    // iOOH saved reports and Slide Creation: saved as the staff member (redirect-to-staff), recorded.
    {
        kind: 'iooh_report_save',
        methods: ['POST'],
        pattern: /^\/api\/studio\/v1\/iooh-saved-reports$/,
        record: 'api_write',
    },
    {
        kind: 'iooh_report_save',
        methods: ['PATCH'],
        pattern: new RegExp(`^/api/studio/v1/iooh-saved-reports/${ID}$`),
        record: 'api_write',
    },
    {
        kind: 'slide_creation_save',
        methods: ['POST'],
        pattern: /^\/api\/slide-creation\/projects$/,
        record: 'api_write',
        ref: (_m, body, path) => str(body?.id) ?? path,
        detail: (_m, body) => ({ project: str(body?.project) }),
    },
    // The deck build: its server meters `observe/pptx-export` as the caller (§7),
    // so it is recorded under the same ref as the browser's PPTX metering (D23).
    {
        kind: 'slide_creation_build',
        methods: ['POST'],
        pattern: /^\/api\/slide-creation\/canvas-build$/,
        record: 'api_write',
        ref: () => 'pptx-export',
        detail: () => ({ functionKey: 'pptx-export', exportPath: 'slide-creation' }),
    },

    // M4S-5777 X112: the open-end verbatims for a crosstab's file. A read sent as POST, but
    // respondents' own words leaving in a file, so under View-as it is recorded as a data export.
    {
        kind: 'crosstab_verbatims',
        methods: ['POST'],
        pattern: new RegExp(`^/api/crosstab/project/${ID}/verbatims$`),
        record: 'data_export',
        detail: (m, body) => ({
            projectId: safeDecode(m[1]),
            surveyIds: Array.isArray(body?.surveys) ? body.surveys.slice(0, 50) : null,
        }),
    },

    // Reads sent as POST: they compute and write nothing.
    {
        kind: 'crosstab_compute',
        methods: ['POST'],
        pattern: new RegExp(`^/api/crosstab/project/${ID}/computed$`),
        record: 'none',
    },
    {
        kind: 'ai_crosstab_setup',
        methods: ['POST'],
        pattern:
            /^\/api\/studio\/v1\/crosstab\/(?:brand-lift-setup|tpbp-setup|ai-template-setup|variable-draft|dani-plan)$/,
        record: 'none',
    },
    {
        kind: 'iooh_slide_edit',
        methods: ['POST'],
        pattern: /^\/api\/studio\/v1\/iooh-slide-edit$/,
        record: 'none',
    },
    {
        kind: 'slide_creation_assist',
        methods: ['POST'],
        pattern: /^\/api\/slide-creation\/(?:headlines|dani|salience|brand-kit)$/,
        record: 'none',
    },
    {
        kind: 'report_wizard_assist',
        methods: ['POST'],
        pattern: /^\/api\/reports\/convo-signals\/(?:suggest|universe-preview)$/,
        record: 'none',
    },
    {
        kind: 'connect_suggestions',
        methods: ['POST'],
        pattern: /^\/api\/connect\/suggestions$/,
        record: 'none',
    },
    // Dashboards: the Sigma embed token and the staff member's own Sigma
    // membership (a documented approximation, §5). Neither writes client data.
    {
        kind: 'dashboard_embed',
        methods: ['POST'],
        pattern: /^\/api\/encrypt\/dashboard$/,
        record: 'none',
    },
    {
        kind: 'dashboard_access',
        methods: ['POST'],
        pattern: /^\/api\/sigma\/grant-access$/,
        record: 'none',
    },

    // Telemetry: the Slack alert that follows an allowed DANI feedback.
    {
        kind: 'feedback_alert',
        methods: ['POST'],
        pattern: /^\/api\/slack\/post-alert$/,
        record: 'none',
    },
];

const RULES: Record<ViewAsWriteClient, readonly Rule[]> = {
    portal: PORTAL_RULES,
    platform: PLATFORM_RULES,
    microservice: MICROSERVICE_RULES,
    graphql: [],
    next: NEXT_RULES,
};

const READ_METHODS = new Set(['GET', 'HEAD']);

function deny(kind: string | null, path: string): ViewAsWriteDecision {
    return { allowed: false, record: 'none', ref: path, kind };
}

function decideGraphql(operation: string | null | undefined, path: string): ViewAsWriteDecision {
    const fields = (operation ?? '')
        .split(',')
        .map((field) => field.trim())
        .filter(Boolean);
    if (fields.length === 0) return deny('graphql_unknown', path);
    if (fields.every((field) => GRAPHQL_ALLOWED_FIELDS.has(field))) {
        return { allowed: true, record: 'none', ref: fields.join(','), kind: 'view_filter' };
    }
    if (fields.includes('setQuestionVisibility'))
        return deny('question_visibility', fields.join(','));
    return deny('graphql_mutation', fields.join(','));
}

/**
 * The one View-as write rule. Default deny: a write no rule names is refused.
 * GET and HEAD are reads and always allowed; the only recorded read is the
 * document upload's presigned-URL mint (see its rule).
 */
export function isViewAsWriteAllowed(input: ViewAsWriteInput): ViewAsWriteDecision {
    const method = String(input.method || 'GET').toUpperCase();
    const path = normaliseViewAsPath(input.path);

    if (input.client === 'graphql') return decideGraphql(input.operation, path);

    const body = parseViewAsBody(input.body);
    for (const rule of RULES[input.client] ?? []) {
        if (!rule.methods.includes(method as Method)) continue;
        const match = path.match(rule.pattern);
        if (!match) continue;
        if (rule.when && !rule.when(body)) continue;
        if (rule.record === 'block') return deny(rule.kind, path);
        const ref = rule.ref ? rule.ref(match, body, path) : match[1] ? safeDecode(match[1]) : path;
        return {
            allowed: true,
            record: rule.record,
            ref: ref ?? path,
            kind: rule.kind,
            ...(rule.detail ? { detail: rule.detail(match, body) } : {}),
        };
    }

    if (READ_METHODS.has(method)) return { allowed: true, record: 'none', ref: null, kind: null };
    return deny(input.client === 'platform' ? 'platform_write' : null, path);
}
